type GrazingMetrics = {
  animalGroup: string;
  moveIn: string;
  moveOut: string;
  startDryMatter: number;
  endDryMatter: number;
  fieldArea: number;
  moveOutMissing?: boolean;
};

type EventLike = {
  grazingMetrics?: GrazingMetrics;
  details: { label: string; value: string }[];
};

/** Parse a DD/MM/YYYY date string into a millisecond timestamp for comparisons. */
export function parseDMY(s: string): number {
  const [d, m, y] = s.split("/").map(Number);
  return new Date(y, m - 1, d).getTime();
}

/** Convert a DD/MM/YYYY date string to an ISO YYYY-MM-DD string. */
export function dmyToISO(s: string): string {
  const [d, m, y] = s.split("/").map(Number);
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/** Return a deduplicated list of every field name across a set of events. */
export function uniqueFields(events: { fields: string[] }[]): string[] {
  return [...new Set(events.flatMap((ev) => ev.fields))];
}

/** Sort events newest-first by DD/MM/YYYY date. Returns a new array. */
export function sortEventsByDateDesc<T extends { date: string }>(
  events: T[]
): T[] {
  return [...events].sort((a, b) => parseDMY(b.date) - parseDMY(a.date));
}

/** Return true when a DD/MM/YYYY date falls within an ISO date range (inclusive). */
export function isDateInRange(
  date: string,
  dateFrom: string,
  dateTo: string
): boolean {
  const fromTs = dateFrom ? new Date(dateFrom + "T00:00:00").getTime() : 0;
  const toTs = dateTo ? new Date(dateTo + "T23:59:59").getTime() : Infinity;
  const ts = parseDMY(date);
  return ts >= fromTs && ts <= toTs;
}

/** Format the structured details of any event type into a single readable string. */
export function eventDetails(ev: EventLike): string {
  const g = ev.grazingMetrics;
  if (g) {
    const missingPreCover = !g.startDryMatter;
    const hasOut = !!g.moveOut;
    const utilizedPerHa = g.startDryMatter - g.endDryMatter;
    const utilized = Math.round(utilizedPerHa * g.fieldArea);

    const parts = [`Animal group: ${g.animalGroup}`, `Move in: ${g.moveIn}`];

    if (hasOut) {
      parts.push(`Move out: ${g.moveOut}`);
      if (missingPreCover) {
        parts.push("Pre-grazing cover not recorded");
      } else {
        parts.push(
          `Pre-grazing: ${g.startDryMatter.toLocaleString()} kg DM/ha`
        );
        parts.push(`Post-grazing: ${g.endDryMatter.toLocaleString()} kg DM/ha`);
        if (utilizedPerHa >= 0) {
          parts.push(`Utilized: ${utilized.toLocaleString()} kg DM`);
        }
      }
    } else {
      parts.push(
        g.moveOutMissing ? "Move-out date missing" : "Grazing in progress"
      );
    }

    return parts.join(" · ");
  }
  return ev.details.map((d) => `${d.label}: ${d.value}`).join(" · ") || "—";
}

type FilterableEvent = {
  type: string;
  fields: string[];
  date: string;
};

export type FilterParams = {
  selectedTypes: string[];
  selectedFields: string[];
  dateFrom: string;
  dateTo: string;
};

/** Filter a list of events by type, field, and date range. */
export function filterEvents<T extends FilterableEvent>(
  events: T[],
  { selectedTypes, selectedFields, dateFrom, dateTo }: FilterParams
): T[] {
  const fromTs = dateFrom ? new Date(dateFrom).getTime() : 0;
  const toTs = dateTo ? new Date(dateTo + "T23:59:59").getTime() : Infinity;
  return events.filter((ev) => {
    if (selectedTypes.length > 0 && !selectedTypes.includes(ev.type))
      return false;
    if (
      selectedFields.length > 0 &&
      !ev.fields.some((f) => selectedFields.includes(f))
    )
      return false;
    const evTs = parseDMY(ev.date);
    if (evTs < fromTs || evTs > toTs) return false;
    return true;
  });
}

/** Count how many filter dimensions are actively set. */
export function countActiveFilters(params: FilterParams): number {
  let count = 0;
  if (params.selectedTypes.length > 0) count++;
  if (params.selectedFields.length > 0) count++;
  if (params.dateFrom) count++;
  if (params.dateTo) count++;
  return count;
}

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** Format an ISO date string ("2026-03-01") as a short human label ("1 Mar"). */
export function fmtISO(s: string): string {
  if (!s) return "…";
  const [, m, d] = s.split("-").map(Number);
  return `${d} ${MONTH_NAMES[m - 1]}`;
}

/** Build a human-readable date range label for the filter bar. */
export function formatDateRangeLabel(dateFrom: string, dateTo: string): string {
  if (!dateFrom && !dateTo) return "All dates";
  return `${fmtISO(dateFrom)} – ${fmtISO(dateTo)}`;
}

/** Group events into a Map keyed by field name (one event may appear in multiple keys). */
export function groupEventsByField<T extends { fields: string[] }>(
  events: T[]
): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const ev of events) {
    for (const f of ev.fields) {
      const existing = map.get(f) ?? [];
      existing.push(ev);
      map.set(f, existing);
    }
  }
  return map;
}

type CsvableEvent = {
  date: string;
  type: string;
  fields: string[];
  details: { label: string; value: string }[];
  grazingMetrics?: GrazingMetrics;
};

/** Build the raw CSV string for a list of events. Does not touch the DOM. */
export function buildCsvContent(
  events: CsvableEvent[],
  typeLabels: Record<string, string>
): string {
  const cols = ["Date", "Type", "Fields", "Details"];
  const q = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const rows = events.map((ev) =>
    [
      ev.date,
      typeLabels[ev.type] ?? ev.type,
      ev.fields.join("; "),
      eventDetails(ev),
    ]
      .map(q)
      .join(",")
  );
  return [cols.map(q).join(","), ...rows].join("\n");
}

type PdfEvent = CsvableEvent;

/** Build one HTML table row for the print/PDF report. */
export function buildPdfEventRow(
  ev: PdfEvent,
  typeLabels: Record<string, string>,
  includeFields = false
): string {
  return `<tr><td>${ev.date}</td><td>${typeLabels[ev.type] ?? ev.type}</td>${
    includeFields ? `<td>${ev.fields.join(", ")}</td>` : ""
  }<td style="color:#444">${eventDetails(ev)}</td></tr>`;
}

/** Build the HTML table header row for the print/PDF report. */
export function buildPdfTableHead(includeFields: boolean): string {
  return `<thead><tr><th>Date</th><th>Type</th>${
    includeFields ? "<th>Field(s)</th>" : ""
  }<th>Details</th></tr></thead>`;
}

/** Build the inner body content (tables/sections) for the print/PDF report. */
export function buildPdfBody(
  events: PdfEvent[],
  typeLabels: Record<string, string>,
  groupByField: boolean
): string {
  if (!groupByField) {
    return `<table>${buildPdfTableHead(true)}<tbody>${events
      .map((ev) => buildPdfEventRow(ev, typeLabels, true))
      .join("")}</tbody></table>`;
  }
  const fieldMap = groupEventsByField(events);
  return Array.from(fieldMap.entries())
    .map(
      ([field, evs]) =>
        `<div class="section"><div class="section-title">${field}</div>` +
        `<table>${buildPdfTableHead(false)}<tbody>${evs
          .map((ev) => buildPdfEventRow(ev, typeLabels))
          .join("")}</tbody></table></div>`
    )
    .join("");
}
